import React, { useState } from "react"
import { Card, CardBody, Button, Alert, Badge } from "reactstrap"

const APIDebugger = () => {
  const [results, setResults] = useState([])
  const [loading, setLoading] = useState(false)

  const addResult = (test, status, message, data = null) => {
    setResults(prev => [...prev, {
      test,
      status,
      message,
      data,
      timestamp: new Date().toLocaleTimeString()
    }])
  }

  const clearResults = () => {
    setResults([])
  }

  const runTests = async () => {
    setLoading(true)
    clearResults()

    // Test 1: Check localStorage
    try {
      const authUser = JSON.parse(localStorage.getItem("authUser") || "{}")
      if (authUser && authUser.access_token) {
        addResult("Authentication", "success", "Auth token found", {
          token_length: authUser.access_token.length,
          user_id: authUser.id || "N/A"
        })
      } else {
        addResult("Authentication", "error", "No auth token found")
        setLoading(false)
        return
      }

      // Test 2: Test API connectivity
      try {
        // const testResponse = await fetch("https://vipmalldemobackend.erpca.shop/public/api/rfq-dashboard-test", {
        const testResponse = await fetch("https://api.v1malls.com/api/rfq-dashboard-test", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${authUser.access_token}`,
            "Accept": "application/json"
          },
          body: JSON.stringify({ test: true, timestamp: new Date().toISOString() })
        })

        const testData = await testResponse.json()
        
        if (testResponse.ok) {
          addResult("API Connectivity", "success", `Test endpoint responded: ${testResponse.status}`, testData)
        } else {
          addResult("API Connectivity", "error", `Test endpoint failed: ${testResponse.status}`, testData)
        }
      } catch (connectError) {
        addResult("API Connectivity", "error", `Network error: ${connectError.message}`)
      }

      // Test 3: Test dashboard overview endpoint
      try {
        // const dashboardResponse = await fetch("https://vipmalldemobackend.erpca.shop/public/api/rfq-dashboard-overview", {
        const dashboardResponse = await fetch("https://api.v1malls.com/api/rfq-dashboard-overview", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${authUser.access_token}`,
            "Accept": "application/json"
          },
          body: JSON.stringify({
            start_date: "2024-01-01",
            end_date: "2024-12-31"
          })
        })

        const dashboardData = await dashboardResponse.json()
        
        if (dashboardResponse.ok) {
          addResult("Dashboard API", "success", `Dashboard endpoint responded: ${dashboardResponse.status}`, {
            total_rfqs: dashboardData.data?.summary?.total_rfqs || "N/A",
            status: dashboardData.status,
            message: dashboardData.message
          })
        } else {
          addResult("Dashboard API", "error", `Dashboard endpoint failed: ${dashboardResponse.status}`, dashboardData)
        }
      } catch (dashboardError) {
        addResult("Dashboard API", "error", `Dashboard request error: ${dashboardError.message}`)
      }

      // Test 4: Test using the helper function
      try {
        const { getRFQDashboardOverview } = await import("../../../../helpers/rfq_dashboard_api")
        console.log("Testing getRFQDashboardOverview helper...")

        const helperResponse = await getRFQDashboardOverview({
          start_date: "2024-01-01",
          end_date: "2024-12-31"
        })

        console.log("Helper response:", helperResponse)
        console.log("Helper response type:", typeof helperResponse)
        console.log("Helper response.data:", helperResponse?.data)
        console.log("Helper response.data.status:", helperResponse?.data?.status)

        if (helperResponse && helperResponse.status) {
          addResult("Helper Function", "success", "Helper function worked", {
            total_rfqs: helperResponse.data?.summary?.total_rfqs || "N/A",
            response_structure: "response.status = " + helperResponse.status
          })
        } else {
          addResult("Helper Function", "error", "Helper function failed - unexpected response structure", {
            response: helperResponse,
            has_status: !!helperResponse?.status,
            status_value: helperResponse?.status
          })
        }
      } catch (helperError) {
        console.error("Helper function error:", helperError)
        addResult("Helper Function", "error", `Helper function error: ${helperError.message}`, {
          error_type: helperError.constructor.name,
          stack: helperError.stack
        })
      }

    } catch (error) {
      addResult("General", "error", `Unexpected error: ${error.message}`)
    }

    setLoading(false)
  }

  const getStatusColor = (status) => {
    switch (status) {
      case "success": return "success"
      case "error": return "danger"
      case "warning": return "warning"
      default: return "secondary"
    }
  }

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="mb-0">🔧 RFQ Dashboard API Debugger</h5>
          <div>
            <Button 
              color="primary" 
              onClick={runTests} 
              disabled={loading}
              className="me-2"
            >
              {loading ? "Running Tests..." : "Run API Tests"}
            </Button>
            <Button color="secondary" onClick={clearResults} disabled={loading}>
              Clear
            </Button>
          </div>
        </div>

        {results.length === 0 && !loading && (
          <Alert color="info">
            Click "Run API Tests" to diagnose API connectivity issues.
          </Alert>
        )}

        {results.map((result, index) => (
          <Alert key={index} color={getStatusColor(result.status)} className="mb-2">
            <div className="d-flex justify-content-between align-items-start">
              <div>
                <strong>{result.test}</strong>
                <Badge color={getStatusColor(result.status)} className="ms-2">
                  {result.status.toUpperCase()}
                </Badge>
                <div className="mt-1">{result.message}</div>
                {result.data && (
                  <details className="mt-2">
                    <summary style={{ cursor: "pointer" }}>View Details</summary>
                    <pre className="mt-2 p-2 bg-light rounded" style={{ fontSize: "12px" }}>
                      {JSON.stringify(result.data, null, 2)}
                    </pre>
                  </details>
                )}
              </div>
              <small className="text-muted">{result.timestamp}</small>
            </div>
          </Alert>
        ))}

        {loading && (
          <Alert color="info">
            <div className="d-flex align-items-center">
              <div className="spinner-border spinner-border-sm me-2" role="status">
                <span className="visually-hidden">Loading...</span>
              </div>
              Running diagnostic tests...
            </div>
          </Alert>
        )}
      </CardBody>
    </Card>
  )
}

export default APIDebugger
